import React, {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import * as lib from '~/dependencies';
import { CircularProgress } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { electron, getToken, log } from '~/helpers';
import { VIDEO_WS_SERVER } from '~/config/config';
import { RootState } from '~/store/config';
import { BroadcastData } from '~/interfaces';
import {
  startNewVideoCall,
  updateVideoCall,
  unregisterUA,
  registerUA,
  setVideoCallActivity,
} from '~/store/actions';
import { IVideoCallCurrent } from '~/store/reducers/callCurrent';
import { FullScreenLoaderModal } from '~/components';

const renderStartCall = (message: string) => {
  return (
    <>
      <span style={{ fontWeight: '600' }}>{message}</span>
      <CircularProgress className="loading" size={26} />
    </>
  );
};

interface VideoProviderProps {
  children: ReactNode;
}

interface IVideoContext {
  socket: RefObject<Socket | null>;
  startVideo: (account: string, extension: string) => void;
  stopVideo: () => void;
  acceptCall: (data: IVideoCallCurrent) => void;
  startCall: (data: IVideoCallCurrent) => void;
  rejectCall: (data: IVideoCallCurrent) => void;
}

export const VideoContext = createContext<IVideoContext>(null!);

export default function VideoProvider({ children }: VideoProviderProps) {
  // REFS
  const socket = useRef<Socket | null>(null);
  const broadCastChannel = useRef<BroadcastChannel | null>(null);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  // SELECTORS
  const accountId = useSelector((state: RootState) => state.auth?.account_id);
  const userExtension = useSelector(
    (state: RootState) => state.auth?.user_extension
  );
  const isBusy = useSelector((state: RootState) => state.system?.isBusy);

  // MEMOS
  const isBusyMemo = useMemo(() => isBusy, [isBusy]);

  // STATES
  const [isSessionInit, setIsSessionInit] = useState<boolean>(false);

  const startVideo = useCallback(() => {
    if (!window.isElectron) {
      return;
    }

    if (socket.current && socket.current.connected) {
      log.info(`NUACOM-LOG: [VIDEO] - Video WebSocket is already connected`);

      return;
    }

    electron.log(`NUACOM-LOG: [VIDEO] - Connecting Video WebSocket.`);
    const token = getToken();

    socket.current = io(VIDEO_WS_SERVER, {
      transports: ['websocket'],
      reconnectionDelay: 7000,
      auth: { account: accountId, extension: userExtension, token },
    });

    // Socket events from the signaling server
    socket.current.on('connect', () => {
      electron.log(`NUACOM-LOG: [VIDEO] - Video WebSocket is connected`);
    });

    socket.current.on('connect_error', err => {
      electron.log(
        `NUACOM-LOG: [VIDEO] - Video connection error. Reason: ${err.message}`
      );
    });

    socket.current.on('disconnect', () => {
      electron.log(`NUACOM-LOG: [VIDEO] - Video WebSocket disconnected`);
    });

    socket.current.on('invite', event => {
      const { callId, fromName } = event;
      const data = {
        call: {
          id: callId,
          from: fromName,
          isVideoCall: true,
        },
        activeCalls: 1,
      };

      dispatch(startNewVideoCall(event));
      electron.incomingCall(data);
    });

    socket.current.on('hangup', event => {
      if (broadCastChannel.current) {
        const { callId } = event;
        electron.hideNotification(callId);
        // dismiss the incoming call banner
        dispatch(updateVideoCall(null));
        broadCastChannel.current.postMessage(event);
      }
    });

    socket.current.on('reject', event => {
      if (broadCastChannel.current) {
        broadCastChannel.current.postMessage(event);
      }
    });
  }, [dispatch, accountId, userExtension]);

  const stopVideo = () => {
    log.info('NUACOM_LOG: Stopping Video WebSocket.');
    if (socket.current && socket.current.disconnected) {
      log.info(`NUACOM_LOG: Video WebSocket is already disconnected`);

      return;
    }

    // @ts-ignore
    socket.current.disconnect();
    electron.endVideoCall();
  };

  const handleCallStatus = () => {
    dispatch(unregisterUA());
    dispatch(setVideoCallActivity(true));
  };

  const handleBroadcastChannelMessages = (event: MessageEvent) => {
    if (!socket.current) return;

    const data: BroadcastData = event.data as any;

    switch (data.type) {
      case 'invite':
        socket.current.emit('invite', data);
        break;
      case 'hangup':
        socket.current.emit('hangup', data);
        break;
      case 'callStatus':
        handleCallStatus();
        break;
      default:
    }
  };

  const getErrorMessage = (errorCode: string, destExt: string) => {
    let errorMessage = '';

    switch (errorCode) {
      case 'dst_not_found':
        errorMessage = `${t('screens.video.noAvailable', {
          remoteUserName: destExt,
        })}`;
        break;
      case 'call_failed':
        errorMessage = `${t('screens.video.videoCallFailed')}`;
        break;
      default:
    }

    return errorMessage;
  };

  const acceptCall = (data: IVideoCallCurrent) => {
    if (socket.current && data) {
      // Tell to other extension instances that this one has been accepted.
      socket.current.emit('accepted', data);
    }
  };

  const startCall = async (data: IVideoCallCurrent) => {
    try {
      setIsSessionInit(true);

      if (socket.current && data) {
        const response = await socket.current
          .timeout(7000)
          .emitWithAck('invite', data);
        const { errorMessage, roomUrl } = response;

        if (roomUrl) {
          electron.startVideoCall({ ...data, roomUrl });
          setTimeout(() => {
            setIsSessionInit(false);
          }, 1000);

          return;
        }

        setIsSessionInit(false);
        lib.ToastMessage(
          'error',
          getErrorMessage(errorMessage, data.toName),
          7000
        );

        return;
      }

      lib.ToastMessage(
        'error',
        getErrorMessage('call_failed', data.toName),
        7000
      );

      setTimeout(() => {
        setIsSessionInit(false);
      }, 2000);
    } catch (err) {
      electron.log(
        `NUACOM-LOG: [VIDEO] - Failure to start video call id: ${data.callId}. Reason: ${err}`
      );
      setIsSessionInit(false);
      lib.ToastMessage(
        'error',
        getErrorMessage('call_failed', data.toName),
        7000
      );
    }
  };

  const rejectCall = (data: IVideoCallCurrent) => {
    if (socket.current && data) {
      const { from, fromName, to, toName, isInviter } = data;

      socket.current.emit('reject', {
        ...data,
        type: 'reject',
        from: to,
        fromName: toName,
        to: from,
        toName: fromName,
        isInviter: !isInviter,
      });
    }
  };

  // Broadcast channel between the two web context main(Nuacom App) and video.
  const startBroadcastChannel = useCallback(() => {
    broadCastChannel.current = new BroadcastChannel('video_channel');
    broadCastChannel.current.onmessage = handleBroadcastChannelMessages;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUARegistration = () => {
    dispatch(registerUA());
    dispatch(setVideoCallActivity(false));
  };

  // EFFECT'S
  useEffect(() => {
    if (!socket.current) return;

    if (isBusyMemo) {
      stopVideo();
    } else {
      startVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusyMemo]);

  useEffect(() => {
    if (!broadCastChannel.current) {
      startBroadcastChannel();
    }

    // Used to re-register the UA when the video browser window closes.
    if (window.isElectron) {
      window.ipcRenderer.on('uaRegistration', handleUARegistration);
    }

    return () => {
      if (window.isElectron) {
        window.ipcRenderer.removeListener(
          'uaRegistration',
          handleUARegistration
        );

        // Disconnect when the user does log out
        stopVideo();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accountId && userExtension && !socket.current) {
      startVideo();
    }
  }, [accountId, userExtension, startVideo]);

  return (
    <VideoContext.Provider
      value={{
        socket,
        startVideo,
        stopVideo,
        acceptCall,
        startCall,
        rejectCall,
      }}>
      <FullScreenLoaderModal open={isSessionInit}>
        {renderStartCall(t('components.fullScreenLoaderModal.startVideoCall'))}
      </FullScreenLoaderModal>
      {children}
    </VideoContext.Provider>
  );
}
